// First-visit attestation modal for /catalogue.
//
// Anyone landing on the research catalogue must affirmatively
// acknowledge two things before seeing any product surface:
//   1. They are 18 or older.
//   2. Products are for research purposes only, NOT for human consumption.
//
// Persisted per-device in localStorage. This is a UX click-through, not
// user data — so per-device cookie persistence is fine and does not
// contradict the "no localStorage for user data" rule we enforced on
// the injection toolkit.
//
// Renders as a full-page ink overlay that blocks the catalogue below
// until the visitor either acknowledges or leaves the site.
import { useState } from "react";
import Button from "@/components/Button";
import { C } from "@/lib/brand";
import { RESEARCH_NAVY, RESEARCH_NAVY_DEEP } from "@/components/research/ResearchHero";

const STORAGE_KEY = "voya9.catalogueAttested.v1";

// Callers use this to gate rendering. Returns true if the visitor has
// previously acknowledged on this device.
export function hasAttested() {
  try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
}

export default function CatalogueAttestation({ onAcknowledge }) {
  const [age, setAge] = useState(false);
  const [research, setResearch] = useState(false);
  const ready = age && research;

  const acknowledge = () => {
    if (!ready) return;
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* private mode */ }
    onAcknowledge?.();
  };

  const leave = () => {
    // No dark pattern — visitor gets to actually leave. History back if
    // there's somewhere to go, else route to the employer landing so we
    // don't drop them on a browser default-tab.
    if (window.history.length > 1) window.history.back();
    else window.location.assign("/for-employers");
  };

  return (
    <div
      data-testid="catalogue-attestation-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="catalogue-attestation-title"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: RESEARCH_NAVY_DEEP,
        color: C.cream,
        overflowY: "auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "48px 24px",
      }}
    >
      <div style={{ maxWidth: 560, width: "100%" }}>
        <div
          style={{
            fontFamily: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
            fontSize: 10,
            letterSpacing: "0.3em",
            color: C.peach,
            textTransform: "uppercase",
            marginBottom: 22,
          }}
        >
          ▪ RESEARCH CATALOGUE · ACCESS ACKNOWLEDGEMENT
        </div>
        <h1
          id="catalogue-attestation-title"
          style={{
            margin: 0,
            fontSize: 40,
            fontWeight: 800,
            letterSpacing: "-0.02em",
            lineHeight: 1.05,
            color: C.cream,
            fontFamily: '"DM Sans", sans-serif',
          }}
        >
          Two things,{" "}
          <span
            style={{
              fontFamily: '"Instrument Serif", Georgia, serif',
              fontStyle: "italic",
              fontWeight: 400,
              color: C.peach,
            }}
          >
            before you continue.
          </span>
        </h1>
        <p style={{ marginTop: 22, fontSize: 15, lineHeight: 1.65, color: "rgba(250,246,240,0.75)" }}>
          The compounds listed on this catalogue are supplied for research purposes only. They are <strong style={{ color: C.cream }}>not</strong> approved for clinical, therapeutic, diagnostic, or human-consumption use. Please acknowledge the following before continuing.
        </p>

        <div style={{ marginTop: 32, display: "flex", flexDirection: "column", gap: 14 }}>
          <AckRow
            testId="catalogue-attestation-age"
            checked={age}
            onChange={setAge}
            label={<>I am <strong>18 years of age or older</strong>.</>}
          />
          <AckRow
            testId="catalogue-attestation-research"
            checked={research}
            onChange={setResearch}
            label={<>I understand these products are for <strong>research purposes only</strong> and not for human consumption.</>}
          />
        </div>

        <div style={{ marginTop: 40, display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Button
            variant="peach"
            size="lg"
            disabled={!ready}
            onClick={acknowledge}
            testId="catalogue-attestation-acknowledge"
          >
            I ACKNOWLEDGE →
          </Button>
          <button
            type="button"
            onClick={leave}
            data-testid="catalogue-attestation-leave"
            style={{
              background: "transparent",
              border: `1px solid rgba(250,246,240,0.35)`,
              color: "rgba(250,246,240,0.85)",
              padding: "16px 22px",
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              cursor: "pointer",
              fontFamily: "inherit",
              textTransform: "uppercase",
            }}
          >
            LEAVE SITE
          </button>
        </div>

        <p
          style={{
            marginTop: 34,
            fontSize: 11.5,
            letterSpacing: "0.05em",
            color: "rgba(250,246,240,0.5)",
            lineHeight: 1.6,
          }}
        >
          All sales on the research catalogue are final. Returns and refunds are not available.
          Physician-supervised programs with clinical oversight are available separately at{" "}
          <a
            href="/for-employers"
            style={{ color: C.peach, textDecoration: "underline" }}
            data-testid="catalogue-attestation-employer-link"
          >
            /for-employers
          </a>.
        </p>
      </div>
    </div>
  );
}

function AckRow({ testId, checked, onChange, label }) {
  return (
    <label
      data-testid={testId}
      style={{
        display: "flex",
        gap: 14,
        alignItems: "flex-start",
        padding: "14px 16px",
        background: "rgba(250,246,240,0.05)",
        border: `1px solid ${checked ? C.peach : "rgba(250,246,240,0.15)"}`,
        cursor: "pointer",
        transition: "border-color 120ms ease, background 120ms ease",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={`${testId}-input`}
        style={{
          marginTop: 3,
          width: 18,
          height: 18,
          accentColor: RESEARCH_NAVY,
          cursor: "pointer",
        }}
      />
      <span style={{ fontSize: 15, lineHeight: 1.5, color: C.cream }}>{label}</span>
    </label>
  );
}
